import React from 'react';
import { Link } from 'react-router-dom';
import { useSelector } from 'react-redux';
import { selectAuthenticatedUser } from 'src/store/selectors/auth';
import { PATHS } from 'src/common/constants';
import Callout from 'src/components/InformationCallout/Callout';
import LinkPreviewCard from 'src/components/LinkPreviewCard/LinkPreviewCard';
import Skeleton from 'src/components/Skeleton/Skeleton';
import {
  ICalendarEventData,
  KEY_PICKUP_EVENT_TYPE,
  WALKTHROUGH_EVENT_TYPE,
} from 'src/pages/Calendar/types';
import {
  IUseCalcomConnection,
  useCalcomOauthConnection,
} from '../../CustomHooks/useCalcomOauthConnection';
import CalcomConnectionBadge from 'src/components/CalcomConnectionBadge/CalcomConnectionBadge';
import CalcomEventExplainer from '../CalcomEventExplainer/CalcomEventExplainer';
import CalcomHostCard from '../CalcomHostCard/CalcomHostCard';
import ConnectedCalendars from '../ConnectedCalendars/ConnectedCalendars';
import calcomLogo from '../../calcom-logo.svg';
import calcomConnectCalendarPreview from '../../assets/calcom-connect-calendar-preview.png';
import './calcom-event-details.scss';

export const CalcomEventPill = () => (
  <span className="calcom-event-pill">
    Scheduled in
    <img className="calcom-event-pill-logo" src={calcomLogo} alt="Cal.com" />
  </span>
);

interface ICalcomEventDetailsSectionProps {
  event: ICalendarEventData;
  hideExplainer?: boolean;
}

const CalcomEventDetailsSection = ({
  event,
  hideExplainer
}: ICalcomEventDetailsSectionProps) => {
  const user: any = useSelector(selectAuthenticatedUser);

  const {
    isConnected,
    connectedCalendars,
    isLoadingStatus,
  }: IUseCalcomConnection = useCalcomOauthConnection();

  const isHost: boolean =
    user?.id != null &&
    event.eventHostUserId != null &&
    String(user.id) === String(event.eventHostUserId);

  const hasNoCalendars: boolean = connectedCalendars.length === 0;

  const isKeyPickup: boolean = event.juniperEventType === KEY_PICKUP_EVENT_TYPE;

  const bookingTypeLabel: string =
    event.juniperEventType === KEY_PICKUP_EVENT_TYPE
      ? 'Key-pickup'
      : event.juniperEventType === WALKTHROUGH_EVENT_TYPE
        ? 'Walkthrough'
        : 'Cal.com';

  return (
    <div className="calcom-event-details">
      {!hideExplainer && (
        <CalcomEventExplainer juniperEventType={event.juniperEventType} />
      )}

      {isLoadingStatus && <Skeleton variant="rect" height="8rem" />}

      {!isLoadingStatus && isConnected && isKeyPickup && (
        <Callout
          variant="info"
          title="Manage your key pickups"
          subtitle="Track which units have been invited, who's booked, and send new key-pickup invites — all from the key pickups tab."
        >
          <Link
            className="calcom-event-details-connect-link"
            to={PATHS.CALENDAR_CALCOM_KEY_PICKUPS}
          >
            Go to key pickups
          </Link>
        </Callout>
      )}

      {!isLoadingStatus && !isConnected && (
        <Callout
          variant="warning"
          title={<CalcomConnectionBadge isConnected={false} />}
          subtitle={
            <>
              {bookingTypeLabel} bookings run on the Cal.com account of the
              admin or property developer who set them up — so this one is
              hosted on their account, not yours. Connecting your own Cal.com
              account (optional) lets you create these bookings, manage
              reschedules and cancellations for the ones you host, and see which
              of your calendars they sync to. It won&apos;t change who owns this
              booking.
            </>
          }
        >
          <Link
            className="calcom-event-details-connect-link"
            to={PATHS.CALENDAR_CALCOM}
          >
            Connect Cal.com
          </Link>
        </Callout>
      )}

      {!isLoadingStatus && isConnected && isHost && hasNoCalendars && (
        <Callout
          variant="error"
          title="No calendars connected to Cal.com"
          subtitle={
            <>
              You&apos;re the host of this booking, but your Cal.com account has
              no calendars linked — so availability can&apos;t be checked and
              this booking isn&apos;t being written to any of your calendars.
              Link one in Cal.com to fix this.
            </>
          }
        >
          <LinkPreviewCard
            href="https://cal.com/help/quick-start/calendar"
            title="Connect your calendars"
            description="Link Google, Outlook, Apple, and more to your Cal.com account so availability stays in sync."
            siteName="Cal.com Help"
            image={calcomConnectCalendarPreview}
          />
        </Callout>
      )}

      {!isLoadingStatus && isConnected && isHost && !hasNoCalendars && (
        <div className="calcom-event-details-sync">
          <div className="calcom-event-details-sync-header">
            <p className="calcom-event-details-sync-heading">
              Synced to your calendars
            </p>
            <img
              className="calcom-event-details-sync-logo"
              src={calcomLogo}
              alt="Cal.com"
            />
          </div>
          <p className="calcom-event-details-sync-lead">
            You&apos;re the host of this booking, so it&apos;s synced to the
            calendars you&apos;ve connected to Cal.com — any reschedule or
            cancellation keeps them up to date automatically. To add or remove
            calendars, or manage the connected account itself, do it from
            Cal.com&apos;s end — not here in Juniper.
          </p>

          <ConnectedCalendars calendars={connectedCalendars} compact />
        </div>
      )}

      {!isLoadingStatus && isConnected && !isHost && (
        <CalcomHostCard
          heading="Hosted on another Cal.com account"
          lead={
            <>
              This booking is hosted on another team member&apos;s connected
              Cal.com account, so the calendars it syncs to aren&apos;t visible
              to you here. Rescheduling, cancelling, or changing which calendars
              it&apos;s on is managed from that account — reach out to the host
              below if it needs changing.
            </>
          }
          name={event.eventHostName ?? undefined}
          email={event.eventHostEmail ?? undefined}
          role="Cal.com host"
        />
      )}
    </div>
  );
};

export default CalcomEventDetailsSection;